import { NavLink, Link, useNavigate } from "react-router-dom";
import useUserStore from "../../stores/user";
import supabase from "../../utils/supabase";
import { useState, useRef, useEffect } from "react";
import { brandingConfig } from "../../config/branding";
import { useNotifications } from "../../hooks/useNotifications";
import NotificationsDropdown from "../notifications/NotificationsDropdown";
import { useUrl } from "../../composables/useUrl";
import { useT } from "../../stores/uiLang";
import { LangSwitch } from "../LangSwitch";
import { studentLogoutPath } from "../../utils/kidLogin";

// Run E2 J2: header text in English and Spanish (shared UI language).
// "Free Resources" is hidden for Project Explore (PFL high-school page).
const HEADER_T = {
  en: {
    home: "Home",
    curriculum: "Curriculum",
    loginPortal: "Login Portal",
    dashboard: "Dashboard",
    help: "Help",
    logout: "Logout",
    notifications: "Notifications",
    menu: "Menu",
  },
  es: {
    home: "Inicio",
    curriculum: "Currículo",
    loginPortal: "Iniciar sesión",
    dashboard: "Panel",
    help: "Ayuda",
    logout: "Cerrar sesión",
    notifications: "Notificaciones",
    menu: "Menú",
  },
};

export function Header() {
  const user = useUserStore((state) => state.user);
  const navigate = useNavigate();
  const setUser = useUserStore((state) => state.setUser);
  const setProfile = useUserStore((state) => state.setProfile);
  const profile = useUserStore((state) => state.profile);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const boDropdownRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Notifications
  const { unreadCount, isDropdownOpen, setDropdownOpen } = useNotifications();

  const { fetchUserProfile } = useUserStore();
  const { getDashboardUrl } = useUrl();
  const t = useT(HEADER_T);
  useEffect(() => {
    const loadProfile = async () => {
      if (user && !profile) {
        setIsLoading(true);
        try {
          await fetchUserProfile(user);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadProfile();
  }, [user, profile]);

  const handleDocumentClick = (event: MouseEvent) => {
    const isClickOutsideDropdown =
      boDropdownRef.current &&
      !boDropdownRef.current.contains(event.target as Node);
    const isClickOutsideMobileMenu =
      mobileMenuRef.current &&
      !mobileMenuRef.current.contains(event.target as Node);

    if (isClickOutsideDropdown) {
      setIsUserDropdownOpen(false);
    }

    if (isClickOutsideMobileMenu) {
      setIsMobileMenuOpen(false);
    }

    if (
      isClickOutsideDropdown &&
      isClickOutsideMobileMenu &&
      !isUserDropdownOpen &&
      !isMobileMenuOpen
    ) {
      document.removeEventListener("click", handleDocumentClick);
    }
  };

  const toggleMobileMenu = () => {
    const newMenuState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newMenuState);
    if (newMenuState || isUserDropdownOpen) {
      document.addEventListener("click", handleDocumentClick);
    }
  };

  const toggleUserDropdown = () => {
    const newDropdownState = !isUserDropdownOpen;
    setIsUserDropdownOpen(newDropdownState);
    if (newDropdownState || isMobileMenuOpen) {
      document.addEventListener("click", handleDocumentClick);
    }
  };

  const toggleNotificationsDropdown = (event?: React.MouseEvent) => {
    // Prevent event propagation to avoid conflicts with document click handler
    if (event) {
      event.stopPropagation();
    }
    setDropdownOpen(!isDropdownOpen);
  };

  const getInitials = (name: string) => {
    if (!name) return "U"; // return "U" from "User"
    const nameParts = name.split(" ");
    const initials = nameParts
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
    return initials;
  };

  async function logout() {
    // Picture-PIN students (no email) go back to the kid login, everyone else to the login portal.
    const nextPath = studentLogoutPath(user?.email);
    const { error } = await supabase.auth.signOut();
    if (error) console.error("Error logging out:", error.message);

    setUser(undefined);
    setProfile(null);
    navigate(nextPath);
  }

  const renderMobileMenu = () => {
    if (!user) {
      return (
        <div
          className="lg:hidden items-center flex justify-center ml-2"
          ref={mobileMenuRef}
        >
          <LangSwitch className="mr-3" />
          <button
            onClick={toggleMobileMenu}
            className="text-gray-700 hover:text-indigo-600 focus:outline-none"
          >
            {isMobileMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
          {isMobileMenuOpen && (
            <div className="absolute top-16 left-0 w-full bg-white shadow-lg py-2">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `block px-4 py-2 text-sm ${
                    isActive
                      ? "header-active-text font-semibold"
                      : "header-primary-text links"
                  }`
                }
                onClick={() => {
                  toggleMobileMenu();
                  document.removeEventListener("click", handleDocumentClick);
                }}
              >
                {t.home}
              </NavLink>
              <NavLink
                to="/standards"
                className={({ isActive }) =>
                  `block px-4 py-2 text-sm ${
                    isActive
                      ? "header-active-text font-semibold"
                      : "header-primary-text links"
                  }`
                }
                onClick={() => {
                  toggleMobileMenu();
                  document.removeEventListener("click", handleDocumentClick);
                }}
              >
                {t.curriculum}
              </NavLink>
              <NavLink
                to="/login-portal"
                className={({ isActive }) =>
                  `mx-4 my-2 text-sm ${
                    isActive ? "btn btn-small" : "btn btn-small btn-secondary"
                  }`
                }
                onClick={() => {
                  toggleMobileMenu();
                  document.removeEventListener("click", handleDocumentClick);
                }}
              >
                {t.loginPortal}
              </NavLink>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        className="lg:hidden items-center flex justify-center ml-2"
        ref={mobileMenuRef}
      >
        <LangSwitch className="mr-3" />
        <button
          onClick={toggleMobileMenu}
          className="header-primary-text links focus:outline-none"
        >
          {isMobileMenuOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
        {isMobileMenuOpen && (
          <div className="absolute top-16 left-0 w-full bg-white shadow-lg py-2">
            {profile?.role !== "guest" && (
              <NavLink
              to={getDashboardUrl()}
              className={({ isActive }) =>
                `block px-4 py-2 text-sm ${
                  isActive
                    ? "header-active-text font-semibold"
                    : "header-primary-text links"
                }`
              }
              onClick={() => {
                toggleMobileMenu();
                document.removeEventListener("click", handleDocumentClick);
              }}
            >
                {t.dashboard}
              </NavLink>
            )}
            <NavLink
              to="/standards"
              className={({ isActive }) =>
                `block px-4 py-2 text-sm ${
                  isActive
                    ? "header-active-text font-semibold"
                    : "header-primary-text links"
                }`
              }
              onClick={() => {
                toggleMobileMenu();
                document.removeEventListener("click", handleDocumentClick);
              }}
            >
              {t.curriculum}
            </NavLink>
            <NavLink
              to="/support"
              className={({ isActive }) =>
                `block px-4 py-2 text-sm ${
                  isActive
                    ? "header-active-text font-semibold"
                    : "header-primary-text links"
                }`
              }
              onClick={() => {
                toggleMobileMenu();
                document.removeEventListener("click", handleDocumentClick);
              }}
            >
              {t.help}
            </NavLink>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {!user && (
        <header className="header-bg border-b border-[var(--color-headerBorder)] shadow-sm fixed w-full z-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <Link to="/" className="flex items-center cursor-pointer">
                  <img
                    src={brandingConfig.logoPath}
                    className="h-10"
                    alt={brandingConfig.siteTitle}
                  />
                </Link>
              </div>
              <nav className="hidden lg:flex items-center space-x-8">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `text-sm font-medium ${
                      isActive
                        ? "header-active-text font-semibold"
                        : "header-primary-text links"
                    }`
                  }
                >
                  {t.home}
                </NavLink>
                <NavLink
                  to="/standards"
                  className={({ isActive }) =>
                    `text-sm font-medium ${
                      isActive
                        ? "header-active-text font-semibold"
                        : "header-primary-text links"
                    }`
                  }
                >
                  {t.curriculum}
                </NavLink>
                <LangSwitch />
                <NavLink
                  to="/login-portal"
                  className={({ isActive }) =>
                    `text-sm font-medium border rounded-lg px-4 py-2 transition-colors ${
                      isActive ? "btn btn-small" : "btn btn-small btn-secondary"
                    }`
                  }
                >
                  {t.loginPortal}
                </NavLink>
              </nav>
              {renderMobileMenu()}
            </div>
          </div>
        </header>
      )}
      {user && (
        <header className="header-bg border-b border-[var(--color-headerBorder)] shadow-sm fixed w-full z-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center justify-start">
                <Link to="/" className="flex items-center">
                  <img
                    src={brandingConfig.logoPath}
                    alt={brandingConfig.siteTitle}
                    className=" h-10"
                  />
                </Link>
                <nav className="hidden lg:flex items-center space-x-8 ml-10">
                  {profile?.role !== "guest" && (
                    <NavLink
                      to={getDashboardUrl()}
                      className={({ isActive }) =>
                        `text-sm font-medium ${
                          isActive
                            ? "header-active-text font-semibold"
                            : "header-primary-text links"
                        }`
                      }
                    >
                      {t.dashboard}
                    </NavLink>
                  )}
                  {profile?.role !== "guest" && (<NavLink
                    to="/standards"
                    className={({ isActive }) =>
                      `text-sm font-medium ${
                        isActive
                          ? "header-active-text font-semibold"
                          : "header-primary-text links"
                      }`
                    }
                  >
                    {t.curriculum}
                  </NavLink>)}
                  <NavLink
                    to="/support"
                    className={({ isActive }) =>
                      `text-sm font-medium ${
                        isActive
                          ? "header-active-text font-semibold"
                          : "header-primary-text links"
                      }`
                    }
                  >
                    {t.help}
                  </NavLink>
                </nav>
              </div>
              <div className="flex items-center">
                {isLoading ? (
                  <div className="flex items-center gap-4 animate-pulse">
                    <div className="relative h-6 w-6 bg-[var(--color-headerBorder)] rounded" />
                    <div className="hidden lg:inline-block h-4 w-20 bg-[var(--color-headerBorder)] rounded" />
                    <div className="h-8 w-8 bg-[var(--color-headerBorder)] rounded-full" />
                  </div>
                ) : (
                  <>
                    <LangSwitch className="hidden lg:inline-flex mr-4" />
                    <div className="mr-4 relative">
                      <button
                        id="notificationBtn"
                        className={`relative ${
                          isDropdownOpen ? "text-blue-600" : ""
                        }`}
                        onClick={toggleNotificationsDropdown}
                        ref={notificationButtonRef}
                      >
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {unreadCount > 3 ? "3+" : unreadCount}
                          </span>
                        )}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-6 w-6 header-primary-text ${
                            isDropdownOpen ? "text-blue-600" : ""
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                          />
                        </svg>
                      </button>
                      <NotificationsDropdown
                        isOpen={isDropdownOpen}
                        onClose={() => setDropdownOpen(false)}
                      />
                    </div>
                    <button
                      ref={boDropdownRef}
                      className="flex items-center header-primary-text"
                      onClick={toggleUserDropdown}
                    >
                      <span className="hidden lg:inline-block mr-2 text-sm">
                        {profile?.fullname}
                      </span>
                      <div className="relative">
                        <button className="flex items-center justify-center rounded-full bg-[var(--color-headerBorder)] h-8 w-8 focus:outline-none">
                          <span className="text-sm font-medium header-primary-text">
                            {getInitials(profile?.fullname)}
                          </span>
                        </button>

                        {isUserDropdownOpen && (
                          <div className="absolute right-0 mt-2 w-48 header-bg rounded-md shadow-lg py-1 z-50 border border-[var(--color-headerBorder)]">
                            <button
                              onClick={() => {
                                toggleUserDropdown();
                                logout();
                                document.removeEventListener(
                                  "click",
                                  handleDocumentClick
                                );
                              }}
                              className="block w-full text-left px-4 py-2 text-sm header-primary-text links"
                            >
                              {t.logout}
                            </button>
                          </div>
                        )}
                      </div>
                    </button>
                    {renderMobileMenu()}
                  </>
                )}
              </div>
            </div>
          </div>
        </header>
      )}
    </div>
  );
}
